import { useEffect, useCallback, useRef } from 'react';
import { View, StyleSheet } from 'react-native';
import { Typography } from '@banhala/ui-kit';
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withTiming,
  withDelay,
  Easing,
  type SharedValue,
} from 'react-native-reanimated';
import type { HomeTabOverlayText } from 'shared/src/api/homeService/getHomeTabs/types';

const ENTER_DURATION = 500;
const EXIT_DURATION = 500;
const SLIDE_DISTANCE = 14;

const getCharDelay = (charIndex: number, totalChars: number): number => {
  if (totalChars <= 1) return 0;
  const minDelay = 200;
  const maxDelay = 650;
  return minDelay + ((maxDelay - minDelay) * charIndex) / (totalChars - 1);
};

interface TabSlotTextProps {
  overlayText: HomeTabOverlayText;
}

export const TabSlotText = ({ overlayText }: TabSlotTextProps) => {
  const { groups, duration, repeat_count, color } = overlayText;
  const currentGroupIndex = useSharedValue(-1);
  const phaseValue = useSharedValue(0); // 0=idle, 1=enter, 2=show, 3=exit
  const timeoutRefs = useRef<ReturnType<typeof setTimeout>[]>([]);

  const clearTimeouts = useCallback(() => {
    timeoutRefs.current.forEach(clearTimeout);
    timeoutRefs.current = [];
  }, []);

  const addTimeout = useCallback(
    (fn: () => void, ms: number) => {
      const id = setTimeout(fn, ms);
      timeoutRefs.current.push(id);
      return id;
    },
    [],
  );

  useEffect(() => {
    const showDuration = Math.max((duration * 1000) / groups.length, 500);

    const animateGroup = (groupIdx: number, repeatLeft: number) => {
      if (groupIdx >= groups.length) {
        if (repeatLeft > 1) {
          addTimeout(() => animateGroup(0, repeatLeft - 1), 300);
        } else {
          currentGroupIndex.value = -1;
          phaseValue.value = 0;
        }
        return;
      }

      currentGroupIndex.value = groupIdx;
      phaseValue.value = 1; // enter

      addTimeout(() => {
        phaseValue.value = 2; // show
        addTimeout(() => {
          phaseValue.value = 3; // exit
          addTimeout(() => {
            animateGroup(groupIdx + 1, repeatLeft);
          }, EXIT_DURATION);
        }, showDuration);
      }, ENTER_DURATION);
    };

    addTimeout(() => animateGroup(0, repeat_count), 1000);

    return clearTimeouts;
  }, [
    groups,
    duration,
    repeat_count,
    currentGroupIndex,
    phaseValue,
    addTimeout,
    clearTimeouts,
  ]);

  return (
    <View style={styles.container}>
      {groups.map((group, groupIndex) => (
        <SlotGroupText
          key={`${group.text}-${groupIndex}`}
          text={group.text}
          animStyle={group.style}
          color={color}
          groupIndex={groupIndex}
          currentGroupIndex={currentGroupIndex}
          phaseValue={phaseValue}
        />
      ))}
    </View>
  );
};

interface SlotGroupTextProps {
  text: string;
  animStyle: 'sequential' | 'simultaneous';
  color: string;
  groupIndex: number;
  currentGroupIndex: SharedValue<number>;
  phaseValue: SharedValue<number>;
}

const SlotGroupText = ({
  text,
  animStyle,
  color,
  groupIndex,
  currentGroupIndex,
  phaseValue,
}: SlotGroupTextProps) => {
  if (animStyle === 'simultaneous') {
    return (
      <AnimatedChar
        text={text}
        color={color}
        delay={0}
        groupIndex={groupIndex}
        currentGroupIndex={currentGroupIndex}
        phaseValue={phaseValue}
      />
    );
  }

  return (
    <View style={styles.charRow}>
      {text.split('').map((char, charIndex) => (
        <AnimatedChar
          key={`${char}-${charIndex}`}
          text={char}
          color={color}
          delay={getCharDelay(charIndex, text.length)}
          groupIndex={groupIndex}
          currentGroupIndex={currentGroupIndex}
          phaseValue={phaseValue}
        />
      ))}
    </View>
  );
};

const AnimatedChar = ({
  text,
  color,
  delay,
  groupIndex,
  currentGroupIndex,
  phaseValue,
}: {
  text: string;
  color: string;
  delay: number;
  groupIndex: number;
  currentGroupIndex: SharedValue<number>;
  phaseValue: SharedValue<number>;
}) => {
  const opacity = useSharedValue(0);
  const translateY = useSharedValue(SLIDE_DISTANCE);
  const lastPhase = useSharedValue(0);

  useAnimatedStyle(() => {
    const isActive = currentGroupIndex.value === groupIndex;
    const currentPhase = phaseValue.value;

    if (!isActive) {
      if (lastPhase.value !== 0) {
        opacity.value = 0;
        translateY.value = SLIDE_DISTANCE;
        lastPhase.value = 0;
      }
      return { opacity: 0, transform: [{ translateY: SLIDE_DISTANCE }] };
    }

    if (currentPhase === 1 && lastPhase.value !== 1) {
      // enter
      lastPhase.value = 1;
      opacity.value = withDelay(
        delay,
        withTiming(1, {
          duration: Math.max(ENTER_DURATION - delay, 100),
          easing: Easing.linear,
        }),
      );
      translateY.value = withDelay(
        delay,
        withTiming(0, {
          duration: Math.max(ENTER_DURATION - delay, 100),
          easing: Easing.bezier(0.33, 0, 0.67, 1),
        }),
      );
    } else if (currentPhase === 3 && lastPhase.value !== 3) {
      // exit
      lastPhase.value = 3;
      opacity.value = withDelay(
        delay,
        withTiming(0, {
          duration: Math.max(EXIT_DURATION - delay, 100),
          easing: Easing.linear,
        }),
      );
      translateY.value = withDelay(
        delay,
        withTiming(-SLIDE_DISTANCE, {
          duration: Math.max(EXIT_DURATION - delay, 100),
          easing: Easing.bezier(0.33, 0, 0.67, 1),
        }),
      );
    }

    return {
      opacity: opacity.value,
      transform: [{ translateY: translateY.value }],
    };
  });

  const animatedStyle = useAnimatedStyle(() => ({
    opacity: opacity.value,
    transform: [{ translateY: translateY.value }],
  }));

  return (
    <Animated.View style={animatedStyle}>
      <Typography type="subtitle5" rawColor={color}>
        {text}
      </Typography>
    </Animated.View>
  );
};

const styles = StyleSheet.create({
  container: {
    height: 14,
    overflow: 'hidden',
  },
  charRow: {
    flexDirection: 'row',
    position: 'absolute',
  },
});
